import { ZYTE_SPENDING_LIMIT_USD } from "./constants.js";
import { z } from "zod";

//#region src/lib/crawl/config.ts
/** Job types the crawl engine runs for Cardmarket. */
const CRAWL_JOB_TYPES = [
	"cardmarket.catalogue-sync",
	"cardmarket.expansion-harvest",
	"cardmarket.product-offers",
	"cardmarket.fair-prices",
	"cardmarket.sealed-offers"
];
/** Domains the engine crawls. */
const CRAWL_DOMAINS = ["cardmarket.com"];
/** How requests of a domain are fetched. */
const CRAWL_TRANSPORT_MODES = ["http", "browser"];
/** Deployment environments that have their own default configuration. */
const CRAWL_ENVIRONMENTS = [
	"production",
	"dev",
	"local"
];
/** Refresh interval choices of a rule. */
const CRAWL_REFRESH_INTERVALS = [
	"daily",
	"every_3_days",
	"weekly",
	"every_2_weeks",
	"monthly",
	"never"
];
/** Seconds of each interval; `never` has no interval. Monthly means 30 days. */
const CRAWL_REFRESH_INTERVAL_SECONDS = {
	daily: 86400,
	every_3_days: 3 * 86400,
	weekly: 7 * 86400,
	every_2_weeks: 14 * 86400,
	monthly: 30 * 86400,
	never: null
};
/**
* How far a product crawl may go: `base` is the first page only, `full` adds
* the language and variant slices, the condition and seller side refinements
* and the price depth continuation.
*/
const CRAWL_MATRIX_DEPTHS = ["base", "full"];
/** The depth a stored rule saved before the depth choices were reduced reads as. */
const LEGACY_MATRIX_DEPTHS = { slices: "full" };
/** Classification of a product by its last crawl; `unknown` means never crawled. */
const CRAWL_PRODUCT_CLASSIFICATIONS = [
	"thin",
	"liquid",
	"unknown"
];
/** Reasons an entry of a domain's stop list can carry. */
const CRAWL_STOP_REASONS = [
	"operator",
	"throttled",
	"account",
	"wasted_spend",
	"spend_cap",
	"daily_budget"
];
/** Stop reasons set by the engine that a superadmin can resume. */
const CRAWL_RESUMABLE_STOP_REASONS = [
	"throttled",
	"account",
	"wasted_spend"
];
/** Stop reasons the engine adds on its own, each behind its own switch. */
const CRAWL_AUTOMATIC_STOP_REASONS = [
	"throttled",
	"account",
	"wasted_spend",
	"spend_cap",
	"daily_budget"
];
/** Attempts of one request before it counts as failed, when the setting is unset. */
const CRAWL_DEFAULT_MAX_ATTEMPTS_PER_REQUEST = 5;
/** Fewest attempts per request a domain may be set to. */
const CRAWL_MIN_ATTEMPTS_PER_REQUEST = 1;
/** Most attempts per request a domain may be set to. */
const CRAWL_MAX_ATTEMPTS_PER_REQUEST = 10;
/** Settings of one job type. */
const CrawlJobTypeSettingsSchema = z.object({ enabled: z.boolean() });
/** Enabled switch per job type. */
const CrawlJobTypesConfigSchema = z.object({
	"cardmarket.catalogue-sync": CrawlJobTypeSettingsSchema,
	"cardmarket.expansion-harvest": CrawlJobTypeSettingsSchema,
	"cardmarket.product-offers": CrawlJobTypeSettingsSchema,
	"cardmarket.fair-prices": CrawlJobTypeSettingsSchema.default({ enabled: true }),
	"cardmarket.sealed-offers": CrawlJobTypeSettingsSchema.default({ enabled: false })
});
/**
* Which automatic stops may pause a domain. A stop switched off is never
* added, and a present entry of it is lifted when the configuration is saved
* and at the next tick.
*/
const CrawlAutomaticStopsSchema = z.object({
	throttled: z.boolean(),
	account: z.boolean(),
	wasted_spend: z.boolean(),
	spend_cap: z.boolean(),
	daily_budget: z.boolean()
});
/** Every automatic stop switched on. */
function allAutomaticStopsOn() {
	return {
		throttled: true,
		account: true,
		wasted_spend: true,
		spend_cap: true,
		daily_budget: true
	};
}
/** Settings of one domain. */
const CrawlDomainSettingsSchema = z.object({
	paused: z.boolean(),
	targetRatePerMinute: z.number().min(0).meta({ examples: [30] }),
	transportMode: z.enum(CRAWL_TRANSPORT_MODES),
	maxAttemptsPerRequest: z.number().int().min(CRAWL_MIN_ATTEMPTS_PER_REQUEST).max(CRAWL_MAX_ATTEMPTS_PER_REQUEST).default(CRAWL_DEFAULT_MAX_ATTEMPTS_PER_REQUEST).meta({ examples: [5] }),
	automaticStops: CrawlAutomaticStopsSchema.default(allAutomaticStopsOn),
	automaticSlowDown: z.boolean().default(true),
	dailyRequestBudget: z.number().int().min(0).optional().meta({ examples: [2e3] })
});
/** Monthly spend cap in USD, at most the Zyte spending limit. */
const CrawlSpendConfigSchema = z.object({ monthlyCapUsd: z.number().min(0).max(ZYTE_SPENDING_LIMIT_USD, "The monthly spending cap must not be higher than the Zyte spending limit").meta({ examples: [4170] }) });
const PositiveIdListSchema = z.array(z.number().int().positive()).min(1).refine((ids) => new Set(ids).size === ids.length, "Each number can be listed only once");
/** Inclusive bounds; either side may be unset. */
function boundsSchema(unit) {
	return z.object({
		min: z.number().int().min(0).optional(),
		max: z.number().int().min(0).optional()
	}).refine((bounds) => bounds.min !== void 0 || bounds.max !== void 0, `Fill in at least one of the two ${unit} fields`).refine((bounds) => bounds.min === void 0 || bounds.max === void 0 || bounds.min <= bounds.max, `The lower ${unit} must not be higher than the upper one`);
}
/**
* Conjunction of optional conditions on a Cardmarket product, evaluated once
* for each of its finishes: the regular finish and the second one (foil, or
* reverse holo for Pokemon). An unset condition always holds, so an empty
* match matches every finish. A finish without a guide trend, or a product
* without an expansion age, satisfies no condition on it. Every condition but
* the guide trend is the product's; the guide trend is the finish's own.
*/
const CrawlRuleMatchSchema = z.object({
	gameIds: PositiveIdListSchema.optional().meta({ examples: [[1, 3]] }),
	maxExpansionRecencyRank: z.number().int().min(1).optional().meta({ examples: [5] }),
	expansionAgeMonths: boundsSchema("expansion age").optional().meta({ examples: [{ max: 23 }] }),
	guideTrendCents: boundsSchema("trend price").optional().meta({ examples: [{ min: 100 }] }),
	classifications: z.array(z.enum(CRAWL_PRODUCT_CLASSIFICATIONS)).min(1).refine((values) => new Set(values).size === values.length, "Each number of offers option can be ticked only once").optional(),
	expansionIds: PositiveIdListSchema.optional(),
	productIds: PositiveIdListSchema.optional()
});
/** One refresh rule. The first matching rule of the ordered list wins. */
const CrawlRefreshRuleSchema = z.object({
	key: z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "Use lowercase letters and digits joined by dashes, for example latest-expansions").max(48).meta({ examples: ["latest-expansions"] }),
	label: z.string().trim().min(1).max(120).meta({ examples: ["Five newest expansions of each game"] }),
	match: CrawlRuleMatchSchema,
	interval: z.enum(CRAWL_REFRESH_INTERVALS),
	matrixDepth: z.enum(CRAWL_MATRIX_DEPTHS)
});
/** True when a match has no condition, so it matches every product. */
function isEmptyCrawlRuleMatch(match) {
	return Object.values(match).every((condition) => condition === void 0);
}
/**
* Cardmarket language ids whose prices are tracked by default: English (1),
* French (2), German (3), Italian (5), simplified Chinese (6), Japanese (7),
* Korean (10) and traditional Chinese (11).
*/
const CRAWL_DEFAULT_TRACKED_LANGUAGE_IDS = [
	1,
	2,
	3,
	5,
	6,
	7,
	10,
	11
];
/**
* Cardmarket language ids whose prices are tracked. The product crawl keeps a
* per-language cell and spends language requests only on these languages, and
* only when the product page offers them; every other language gets no request
* and no per-language cell, and its offers still count in the all-languages
* cells. With an empty list only the all-languages cells are crawled. A stored
* version without the field reads as the default list.
*/
const CrawlTrackedLanguageIdsSchema = z.array(z.number().int().positive()).refine((ids) => new Set(ids).size === ids.length, "Each language can be listed only once").default(() => [...CRAWL_DEFAULT_TRACKED_LANGUAGE_IDS]).meta({ examples: [[
	1,
	2,
	3,
	5,
	6,
	7,
	10,
	11
]] });
/**
* Cardmarket language ids tracked for sealed products by default: every id of
* the parser's language table.
*/
const CRAWL_DEFAULT_SEALED_TRACKED_LANGUAGE_IDS = [
	1,
	2,
	3,
	4,
	5,
	6,
	7,
	8,
	9,
	10,
	11,
	12,
	13,
	16,
	17
];
/**
* Crawl settings of sealed products: the languages that get a per-language
* cell and how often a sealed product is refreshed. A stored version without
* the field reads as every language, weekly.
*/
const CrawlSealedSettingsSchema = z.object({
	trackedLanguageIds: z.array(z.number().int().positive()).refine((ids) => new Set(ids).size === ids.length, "Each language can be listed only once").default(() => [...CRAWL_DEFAULT_SEALED_TRACKED_LANGUAGE_IDS]).meta({ examples: [[
		1,
		2,
		3,
		5,
		6,
		7,
		10,
		11
	]] }),
	refreshInterval: z.enum(CRAWL_REFRESH_INTERVALS).default("weekly")
});
/** The Cardmarket game exports the crawl knows, by Cardmarket game id. */
const CRAWL_CARDMARKET_GAMES = [
	{
		idGame: 1,
		label: "Magic"
	},
	{
		idGame: 3,
		label: "Yu-Gi-Oh!"
	},
	{
		idGame: 6,
		label: "Pokemon"
	},
	{
		idGame: 13,
		label: "Dragon Ball Super"
	},
	{
		idGame: 16,
		label: "Flesh and Blood"
	},
	{
		idGame: 17,
		label: "Digimon"
	},
	{
		idGame: 18,
		label: "One Piece"
	},
	{
		idGame: 19,
		label: "Lorcana"
	},
	{
		idGame: 21,
		label: "Star Wars Unlimited"
	},
	{
		idGame: 22,
		label: "Riftbound"
	},
	{
		idGame: 23,
		label: "Cyberpunk"
	},
	{
		idGame: 25,
		label: "Sorcery"
	}
];
/** Cardmarket game ids of every known game export, in list order. */
const CRAWL_CARDMARKET_GAME_IDS = CRAWL_CARDMARKET_GAMES.map((game) => game.idGame);
const KNOWN_CARDMARKET_GAME_IDS = new Set(CRAWL_CARDMARKET_GAME_IDS);
/**
* Cardmarket game ids whose catalogue, URL map and offers are crawled. A game
* left out has no catalogue sync, harvest or offers target attempted; its
* stored targets and price rows are kept. An empty list crawls no game. A
* stored version saved before the field existed reads as every known game.
*/
const CrawlEnabledGameIdsSchema = z.array(z.number().int().positive().refine((id) => KNOWN_CARDMARKET_GAME_IDS.has(id), "This game is not one the crawler supports")).refine((ids) => new Set(ids).size === ids.length, "Each game can be listed only once").default(() => [...CRAWL_CARDMARKET_GAME_IDS]).meta({ examples: [[19]] });
const CrawlCardmarketSettingsSchema = z.object({
	enabledGameIds: CrawlEnabledGameIdsSchema,
	trackedLanguageIds: CrawlTrackedLanguageIdsSchema,
	sealed: CrawlSealedSettingsSchema.default(() => ({
		trackedLanguageIds: [...CRAWL_DEFAULT_SEALED_TRACKED_LANGUAGE_IDS],
		refreshInterval: "weekly"
	})),
	refreshRules: z.array(CrawlRefreshRuleSchema).min(1)
}).superRefine((settings, ctx) => {
	const seenKeys = /* @__PURE__ */ new Set();
	settings.refreshRules.forEach((rule, index) => {
		if (seenKeys.has(rule.key)) ctx.addIssue({
			code: "custom",
			path: [
				"refreshRules",
				index,
				"key"
			],
			message: "Another rule already uses this short name"
		});
		seenKeys.add(rule.key);
	});
	const lastIndex = settings.refreshRules.length - 1;
	const last = settings.refreshRules[lastIndex];
	if (last && !isEmptyCrawlRuleMatch(last.match)) ctx.addIssue({
		code: "custom",
		path: [
			"refreshRules",
			lastIndex,
			"match"
		],
		message: "The last rule must have no conditions, so that it catches every remaining product"
	});
});
/** Markup added to the cheapest offer when it has not been configured. */
const FAIR_PRICE_DEFAULT_MARKUP_PERCENT = 5;
/** Largest markup the settings accept, in percent. */
const FAIR_PRICE_MAX_MARKUP_PERCENT = 50;
/** How long a reader of market values may keep using a read of the Fair Price switch. */
const FAIR_PRICE_SWITCH_CACHE_MS = 60 * 1e3;
/**
* Whether EU market values use the Fair Price, and the markup added to the
* cheapest offer before rounding down to 5 cents.
*/
const FairPriceSettingsSchema = z.object({
	enabled: z.boolean(),
	markupPercent: z.number().min(0).max(FAIR_PRICE_MAX_MARKUP_PERCENT).meta({ examples: [5] }),
	sealedEnabled: z.boolean().default(false)
});
/** Fair Price settings of a configuration saved before they existed. */
function defaultFairPriceSettings() {
	return {
		enabled: false,
		markupPercent: FAIR_PRICE_DEFAULT_MARKUP_PERCENT,
		sealedEnabled: false
	};
}
/**
* Admin-editable crawl configuration, stored versioned in `crawl.config`.
* Version 0 is the code default of the environment. Keys the schema does not
* know are dropped when a configuration is parsed.
*/
const CrawlConfigSchema = z.object({
	jobTypes: CrawlJobTypesConfigSchema,
	domains: z.record(z.string().min(1), CrawlDomainSettingsSchema),
	spend: CrawlSpendConfigSchema,
	cardmarket: CrawlCardmarketSettingsSchema,
	fairPrice: FairPriceSettingsSchema.default(defaultFairPriceSettings)
});
function isRecord(value) {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}
/**
* Rewrites the values of a stored configuration that older versions allowed
* and the schema no longer does: a refresh rule depth that no longer exists
* reads as its current equivalent. Anything else is returned unchanged.
*/
function upgradeStoredCrawlConfig(stored) {
	if (!isRecord(stored) || !isRecord(stored.cardmarket)) return stored;
	const { refreshRules } = stored.cardmarket;
	if (!Array.isArray(refreshRules)) return stored;
	return {
		...stored,
		cardmarket: {
			...stored.cardmarket,
			refreshRules: refreshRules.map((rule) => {
				if (!isRecord(rule) || typeof rule.matrixDepth !== "string") return rule;
				const upgraded = LEGACY_MATRIX_DEPTHS[rule.matrixDepth];
				return upgraded ? {
					...rule,
					matrixDepth: upgraded
				} : rule;
			})
		}
	};
}
/**
* Schema of a configuration read back from `crawl.config`: the stored JSON is
* upgraded before it is validated, so a version saved with settings that were
* since removed or with a former rule depth still loads, and the removed keys
* are dropped on the next save.
*/
const StoredCrawlConfigSchema = z.preprocess(upgradeStoredCrawlConfig, CrawlConfigSchema);

//#endregion
export { CRAWL_AUTOMATIC_STOP_REASONS, CRAWL_CARDMARKET_GAMES, CRAWL_CARDMARKET_GAME_IDS, CRAWL_DEFAULT_MAX_ATTEMPTS_PER_REQUEST, CRAWL_DEFAULT_SEALED_TRACKED_LANGUAGE_IDS, CRAWL_DEFAULT_TRACKED_LANGUAGE_IDS, CRAWL_DOMAINS, CRAWL_ENVIRONMENTS, CRAWL_JOB_TYPES, CRAWL_MATRIX_DEPTHS, CRAWL_MAX_ATTEMPTS_PER_REQUEST, CRAWL_MIN_ATTEMPTS_PER_REQUEST, CRAWL_PRODUCT_CLASSIFICATIONS, CRAWL_REFRESH_INTERVALS, CRAWL_REFRESH_INTERVAL_SECONDS, CRAWL_RESUMABLE_STOP_REASONS, CRAWL_STOP_REASONS, CRAWL_TRANSPORT_MODES, CrawlAutomaticStopsSchema, CrawlCardmarketSettingsSchema, CrawlConfigSchema, CrawlDomainSettingsSchema, CrawlEnabledGameIdsSchema, CrawlJobTypeSettingsSchema, CrawlJobTypesConfigSchema, CrawlRefreshRuleSchema, CrawlRuleMatchSchema, CrawlSealedSettingsSchema, CrawlSpendConfigSchema, CrawlTrackedLanguageIdsSchema, FAIR_PRICE_DEFAULT_MARKUP_PERCENT, FAIR_PRICE_MAX_MARKUP_PERCENT, FAIR_PRICE_SWITCH_CACHE_MS, FairPriceSettingsSchema, StoredCrawlConfigSchema, allAutomaticStopsOn, defaultFairPriceSettings, isEmptyCrawlRuleMatch };
//# sourceMappingURL=config.js.map